import * as XLSX from 'xlsx-js-style';
import {
  PORTFOLIO_FISCAL_BUCKETS,
  PortfolioFiscalBucketId,
  PortfolioFiscalBucketKind,
  PortfolioFiscalReportDTO,
} from '@inversor-hub/types';
import {
  transactionCategoryLabels,
  transactionTypeLabels,
} from '@/features/investment-transactions/constants';

const DISCLAIMER =
  'Este resumen no es un cálculo fiscal. Organiza ingresos y gastos por las categorías que usa tu gestor para el IRPF. Faltan partidas que solo tu asesor puede calcular correctamente (como la amortización o la reducción por vivienda habitual). Úsalo para ahorrar tiempo en la declaración, no para sustituirla.';
const EURO_FORMAT = '#,##0.00 [$EUR]';
const COLORS = {
  primary: '1F3A5F',
  header: '5C6B7A',
  light: 'F5F7FA',
  border: 'C8D2DC',
  white: 'FFFFFF',
};

const border = {
  top: { style: 'thin', color: { rgb: COLORS.border } },
  bottom: { style: 'thin', color: { rgb: COLORS.border } },
  left: { style: 'thin', color: { rgb: COLORS.border } },
  right: { style: 'thin', color: { rgb: COLORS.border } },
};

const titleStyle = {
  font: { bold: true, color: { rgb: COLORS.white }, sz: 15 },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.primary } },
  alignment: { horizontal: 'center', vertical: 'center' },
};

const headerStyle = {
  font: { bold: true, color: { rgb: COLORS.white } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.header } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border,
};

const bodyStyle = {
  alignment: { vertical: 'top', wrapText: true },
  border,
};

const totalStyle = {
  font: { bold: true, color: { rgb: COLORS.primary } },
  fill: { patternType: 'solid', fgColor: { rgb: COLORS.light } },
  border,
};

const BUCKET_LABEL_BY_ID = new Map<PortfolioFiscalBucketId, string>(
  PORTFOLIO_FISCAL_BUCKETS.map((bucket) => [bucket.id, bucket.label])
);

function getBucketKindLabel(kind: PortfolioFiscalBucketKind): string {
  switch (kind) {
    case 'income':
      return 'Ingresos';
    case 'deductible':
      return 'Gasto deducible';
    case 'non-deductible':
      return 'No deducible';
    case 'separate':
      return 'Revisar aparte';
    case 'uncategorized':
      return 'Revisar';
  }
}

function ensureCell(sheet: XLSX.WorkSheet, row: number, column: number) {
  const ref = XLSX.utils.encode_cell({ r: row, c: column });
  if (!sheet[ref]) sheet[ref] = { t: 's', v: '' };
  return sheet[ref];
}

function styleRange(
  sheet: XLSX.WorkSheet,
  startRow: number,
  endRow: number,
  startColumn: number,
  endColumn: number,
  style: Record<string, unknown>
) {
  for (let row = startRow; row <= endRow; row++) {
    for (let column = startColumn; column <= endColumn; column++) {
      ensureCell(sheet, row, column).s = style;
    }
  }
}

function formatColumn(
  sheet: XLSX.WorkSheet,
  column: number,
  startRow: number,
  endRow: number,
  format: string
) {
  for (let row = startRow; row <= endRow; row++) {
    const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
    if (cell) cell.z = format;
  }
}

function euros(cents: number) {
  return Number((cents / 100).toFixed(2));
}

function appendSheet(
  workbook: XLSX.WorkBook,
  name: string,
  rows: (string | number)[][],
  widths: number[],
  currencyColumns: number[]
) {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  const lastRow = rows.length - 1;
  const lastColumn = widths.length - 1;
  sheet['!cols'] = widths.map((wch) => ({ wch }));
  sheet['!merges'] = [XLSX.utils.decode_range(`A1:${XLSX.utils.encode_col(lastColumn)}1`)];
  sheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 2, c: 0 },
      e: { r: Math.max(2, lastRow), c: lastColumn },
    }),
  };
  sheet['!freeze'] = { xSplit: 1, ySplit: 3 };
  styleRange(sheet, 0, 0, 0, lastColumn, titleStyle);
  styleRange(sheet, 2, 2, 0, lastColumn, headerStyle);
  if (lastRow >= 3) styleRange(sheet, 3, lastRow, 0, lastColumn, bodyStyle);
  for (const column of currencyColumns) {
    formatColumn(sheet, column, 3, lastRow, EURO_FORMAT);
  }
  XLSX.utils.book_append_sheet(workbook, sheet, name);
  return sheet;
}

export function exportPortfolioFiscalReport(report: PortfolioFiscalReportDTO) {
  const isSingleInvestment = report.investments.length === 1;
  const reportName = isSingleInvestment ? report.investments[0].name : 'cartera';
  const periodLabel = report.quarter ? `${report.year} · T${report.quarter}` : `${report.year}`;
  const scope = isSingleInvestment ? `inmueble · ${reportName}` : 'cartera';
  const workbook = XLSX.utils.book_new();

  const summaryRows: (string | number)[][] = [
    [`Resumen fiscal · ${scope} · ${periodLabel}`, '', '', '', ''],
    [],
    [
      'Inmueble',
      'Código de identificación',
      'Ingresos íntegros',
      'Gastos deducibles',
      'Saldo organizativo',
    ],
    ...report.investments.map((investment) => [
      investment.name,
      investment.identificationCode ?? '',
      euros(investment.totals.grossIncome),
      euros(investment.totals.deductibleExpenses),
      euros(investment.totals.organizationalBalance),
    ]),
    [
      isSingleInvestment ? 'TOTAL INMUEBLE' : 'TOTAL CARTERA',
      '',
      euros(report.totals.grossIncome),
      euros(report.totals.deductibleExpenses),
      euros(report.totals.organizationalBalance),
    ],
    [],
    [DISCLAIMER, '', '', '', ''],
  ];
  const summarySheet = appendSheet(
    workbook,
    isSingleInvestment ? 'Resumen inmueble' : 'Resumen cartera',
    summaryRows,
    [30, 24, 18, 18, 20],
    [2, 3, 4]
  );
  const totalRowIndex = 3 + report.investments.length;
  styleRange(summarySheet, totalRowIndex, totalRowIndex, 0, 4, totalStyle);

  const categoryRows: (string | number)[][] = [
    [`Desglose por categoría IRPF · ${scope} · ${periodLabel}`, '', '', '', '', ''],
    [],
    ['Inmueble', 'Código de identificación', 'Bloque', 'Categoría IRPF', 'Total', 'Movimientos'],
    ...report.investments.flatMap((investment) =>
      investment.buckets
        .filter((bucket) => bucket.transactionCount > 0)
        .map((bucket) => [
          investment.name,
          investment.identificationCode ?? '',
          getBucketKindLabel(bucket.kind),
          bucket.label,
          euros(bucket.total),
          bucket.transactionCount,
        ])
    ),
  ];
  appendSheet(workbook, 'Desglose por categoría', categoryRows, [30, 24, 18, 34, 16, 14], [4]);

  const transactionRows: (string | number)[][] = [
    [`Movimientos · ${scope} · ${periodLabel}`, '', '', '', '', '', '', ''],
    [],
    [
      'Inmueble',
      'Código de identificación',
      'Fecha',
      'Tipo',
      'Concepto',
      'Categoría original',
      'Categoría IRPF',
      'Importe',
    ],
    ...report.investments.flatMap((investment) =>
      investment.transactions.map((transaction) => [
        investment.name,
        investment.identificationCode ?? '',
        new Date(transaction.date).toLocaleDateString('es-ES'),
        transactionTypeLabels[transaction.type],
        transaction.concept ?? 'Sin concepto',
        transactionCategoryLabels[transaction.category],
        BUCKET_LABEL_BY_ID.get(transaction.bucketId) ?? transaction.bucketId,
        euros(transaction.amount),
      ])
    ),
  ];
  appendSheet(workbook, 'Movimientos', transactionRows, [30, 24, 13, 12, 32, 24, 30, 15], [7]);

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const slug =
    reportName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'cartera';
  const periodSlug = report.quarter ? `${report.year}-t${report.quarter}` : `${report.year}`;
  link.download = `resumen-fiscal-${slug}-${periodSlug}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
